import * as z from 'zod';

import { CitizenshipStatus, Ethnicity, Gender } from '../../../Borrower';
import { OptionalPhoneNumber } from '../../../Phone';
import { zodDateOrStringImplyNineteenHundreds } from '../../../utils/Zod';
import { OptionalEmailString } from '../../email';
import { AddressLine, Checkbox, CountryCode, DateString, PersonNameString, SocialSecurityNumber } from '../../fields';

const BorrowerDetailFields = z.object({
  citizenshipStatus: CitizenshipStatus.optional(),
  scraFlag: Checkbox.optional(),
  vip: Checkbox.optional(),
  ficoScore: z.coerce.number().optional(),
  dob: DateString.optional(),
  gender: Gender.optional(),
  ethnicity: Ethnicity.optional(),
});

// Transform to Date
export const BorrowerDetailImportFields = z.object({
  ...BorrowerDetailFields.shape,
  dob: zodDateOrStringImplyNineteenHundreds.optional(),
});

const BorrowerPhoneFields = z.object({
  // home phone number uses existing phone field
  cell: OptionalPhoneNumber,
  work: OptionalPhoneNumber,
  phoneConsent: Checkbox.optional().describe('Defaults to true if value is not provided'),
});

const BorrowerFields = z.object({
  primaryBorrowerFirstName: PersonNameString,
  primaryBorrowerMiddleName: PersonNameString.optional(),
  primaryBorrowerLastName: PersonNameString,
  primaryBorrowerSuffix: z.string().optional().meta({
    example: 'Jr.',
  }),
  primaryBorrowerPhone: OptionalPhoneNumber, // home phone
  primaryBorrowerPhoneDetails: BorrowerPhoneFields.optional(),
  primaryBorrowerEmail: OptionalEmailString,
  primaryBorrowerEmailConsent: Checkbox.optional(),
  primaryBorrowerMailingAddressLine1: AddressLine.optional(),
  primaryBorrowerMailingAddressLine2: AddressLine.optional(),
  primaryBorrowerMailingAddressLine3: AddressLine.optional(),
  primaryBorrowerMailingAddressLine4: AddressLine.optional(),
  primaryBorrowerMailingAddressLocality: z.string().optional(),
  primaryBorrowerMailingAddressRegion: z.string().optional().or(z.literal('')).meta({
    description: 'If primaryBorrowerMailingAddressCountry is US, this should be a two letter state abbreviation.',
    example: 'CA',
  }),
  primaryBorrowerMailingAddressPostcode: z.string().optional().meta({
    description: 'If country is US, this should be a 5 or 9 digit zip code.',
    example: '90210',
  }),
  primaryBorrowerMailingAddressCountry: CountryCode.optional().or(z.literal('')),
  primaryBorrowerSSN: SocialSecurityNumber.optional(),
  primaryBorrowerDetails: BorrowerDetailFields.optional(),
});

export const CoborrowerFields = z.object({
  coBorrower1FirstName: PersonNameString.optional(),
  coBorrower1MiddleName: PersonNameString.optional(),
  coBorrower1LastName: PersonNameString.optional(),
  coBorrower1Suffix: z.string().optional().meta({
    example: 'Jr.',
  }),
  coBorrower1Phone: OptionalPhoneNumber, // home phone
  coBorrower1PhoneDetails: BorrowerPhoneFields.optional(),
  coBorrower1Email: OptionalEmailString,
  coBorrower1EmailConsent: Checkbox.optional(),
  coBorrower1MailingAddressLine1: AddressLine.optional(),
  coBorrower1MailingAddressLine2: AddressLine.optional(),
  coBorrower1MailingAddressLine3: AddressLine.optional(),
  coBorrower1MailingAddressLine4: AddressLine.optional(),
  coBorrower1MailingAddressLocality: z.string().optional(),
  coBorrower1MailingAddressRegion: z.string().optional().or(z.literal('')).meta({
    description: 'If coBorrower1MailingAddressCountry is US, this should be a two letter state abbreviation.',
    example: 'CA',
  }),
  coBorrower1MailingAddressPostcode: z.string().optional().meta({
    description: 'If country is US, this should be a 5 or 9 digit zip code.',
    example: '90210',
  }),
  coBorrower1MailingAddressCountry: CountryCode.optional().or(z.literal('')),
  coBorrower1SSN: SocialSecurityNumber.optional(),
  coBorrower1Details: BorrowerDetailFields.optional(),
  coBorrower2FirstName: PersonNameString.optional(),
  coBorrower2MiddleName: PersonNameString.optional(),
  coBorrower2LastName: PersonNameString.optional(),
  coBorrower2Suffix: z.string().optional().meta({
    example: 'Jr.',
  }),
  coBorrower2Phone: OptionalPhoneNumber,
  coBorrower2PhoneDetails: BorrowerPhoneFields.optional(),
  coBorrower2Email: OptionalEmailString,
  coBorrower2EmailConsent: Checkbox.optional(),
  coBorrower2MailingAddressLine1: AddressLine.optional(),
  coBorrower2MailingAddressLine2: AddressLine.optional(),
  coBorrower2MailingAddressLine3: AddressLine.optional(),
  coBorrower2MailingAddressLine4: AddressLine.optional(),
  coBorrower2MailingAddressLocality: z.string().optional(),
  coBorrower2MailingAddressRegion: z.string().optional().or(z.literal('')).meta({
    description: 'If coBorrower2MailingAddressCountry is US, this should be a two letter state abbreviation.',
    example: 'CA',
  }),
  coBorrower2MailingAddressPostcode: z.string().optional().meta({
    description: 'If country is US, this should be a 5 or 9 digit zip code.',
    example: '90210',
  }),
  coBorrower2MailingAddressCountry: CountryCode.optional().or(z.literal('')),
  coBorrower2SSN: SocialSecurityNumber.optional(),
  coBorrower2Details: BorrowerDetailFields.optional(),
  coBorrower3FirstName: PersonNameString.optional(),
  coBorrower3MiddleName: PersonNameString.optional(),
  coBorrower3LastName: PersonNameString.optional(),
  coBorrower3Suffix: z.string().optional().meta({
    example: 'Jr.',
  }),
  coBorrower3Phone: OptionalPhoneNumber,
  coBorrower3PhoneDetails: BorrowerPhoneFields.optional(),
  coBorrower3Email: OptionalEmailString,
  coBorrower3EmailConsent: Checkbox.optional(),
  coBorrower3MailingAddressLine1: AddressLine.optional(),
  coBorrower3MailingAddressLine2: AddressLine.optional(),
  coBorrower3MailingAddressLine3: AddressLine.optional(),
  coBorrower3MailingAddressLine4: AddressLine.optional(),
  coBorrower3MailingAddressLocality: z.string().optional(),
  coBorrower3MailingAddressRegion: z.string().optional().or(z.literal('')).meta({
    description: 'If coBorrower3MailingAddressCountry is US, this should be a two letter state abbreviation.',
    example: 'CA',
  }),
  coBorrower3MailingAddressPostcode: z.string().optional().meta({
    description: 'If country is US, this should be a 5 or 9 digit zip code.',
    example: '90210',
  }),
  coBorrower3MailingAddressCountry: CountryCode.optional().or(z.literal('')),
  coBorrower3SSN: SocialSecurityNumber.optional(),
  coBorrower3Details: BorrowerDetailFields.optional(),
  coBorrower4FirstName: PersonNameString.optional(),
  coBorrower4MiddleName: PersonNameString.optional(),
  coBorrower4LastName: PersonNameString.optional(),
  coBorrower4Suffix: z.string().optional().meta({
    example: 'Jr.',
  }),
  coBorrower4Phone: OptionalPhoneNumber,
  coBorrower4PhoneDetails: BorrowerPhoneFields.optional(),
  coBorrower4Email: OptionalEmailString,
  coBorrower4EmailConsent: Checkbox.optional(),
  coBorrower4MailingAddressLine1: AddressLine.optional(),
  coBorrower4MailingAddressLine2: AddressLine.optional(),
  coBorrower4MailingAddressLine3: AddressLine.optional(),
  coBorrower4MailingAddressLine4: AddressLine.optional(),
  coBorrower4MailingAddressLocality: z.string().optional(),
  coBorrower4MailingAddressRegion: z.string().optional().or(z.literal('')).meta({
    description: 'If coBorrower4MailingAddressCountry is US, this should be a two letter state abbreviation.',
    example: 'CA',
  }),
  coBorrower4MailingAddressPostcode: z.string().optional().meta({
    description: 'If country is US, this should be a 5 or 9 digit zip code.',
    example: '90210',
  }),
  coBorrower4MailingAddressCountry: CountryCode.optional().or(z.literal('')),
  coBorrower4SSN: SocialSecurityNumber.optional(),
  coBorrower4Details: BorrowerDetailFields.optional(),
  coBorrower5FirstName: PersonNameString.optional(),
  coBorrower5MiddleName: PersonNameString.optional(),
  coBorrower5LastName: PersonNameString.optional(),
  coBorrower5Suffix: z.string().optional().meta({
    example: 'Jr.',
  }),
  coBorrower5Phone: OptionalPhoneNumber,
  coBorrower5PhoneDetails: BorrowerPhoneFields.optional(),
  coBorrower5Email: OptionalEmailString,
  coBorrower5EmailConsent: Checkbox.optional(),
  coBorrower5MailingAddressLine1: AddressLine.optional(),
  coBorrower5MailingAddressLine2: AddressLine.optional(),
  coBorrower5MailingAddressLine3: AddressLine.optional(),
  coBorrower5MailingAddressLine4: AddressLine.optional(),
  coBorrower5MailingAddressLocality: z.string().optional(),
  coBorrower5MailingAddressRegion: z.string().optional().or(z.literal('')).meta({
    description: 'If coBorrower5MailingAddressCountry is US, this should be a two letter state abbreviation.',
    example: 'CA',
  }),
  coBorrower5MailingAddressPostcode: z.string().optional().meta({
    description: 'If country is US, this should be a 5 or 9 digit zip code.',
    example: '90210',
  }),
  coBorrower5MailingAddressCountry: CountryCode.optional().or(z.literal('')),
  coBorrower5SSN: SocialSecurityNumber.optional(),
  coBorrower5Details: BorrowerDetailFields.optional(),
  coBorrower6FirstName: PersonNameString.optional(),
  coBorrower6MiddleName: PersonNameString.optional(),
  coBorrower6LastName: PersonNameString.optional(),
  coBorrower6Suffix: z.string().optional().meta({
    example: 'Jr.',
  }),
  coBorrower6Phone: OptionalPhoneNumber,
  coBorrower6PhoneDetails: BorrowerPhoneFields.optional(),
  coBorrower6Email: OptionalEmailString,
  coBorrower6EmailConsent: Checkbox.optional(),
  coBorrower6MailingAddressLine1: AddressLine.optional(),
  coBorrower6MailingAddressLine2: AddressLine.optional(),
  coBorrower6MailingAddressLine3: AddressLine.optional(),
  coBorrower6MailingAddressLine4: AddressLine.optional(),
  coBorrower6MailingAddressLocality: z.string().optional(),
  coBorrower6MailingAddressRegion: z.string().optional().or(z.literal('')).meta({
    description: 'If coBorrower6MailingAddressCountry is US, this should be a two letter state abbreviation.',
    example: 'CA',
  }),
  coBorrower6MailingAddressPostcode: z.string().optional().meta({
    description: 'If country is US, this should be a 5 or 9 digit zip code.',
    example: '90210',
  }),
  coBorrower6MailingAddressCountry: CountryCode.optional().or(z.literal('')),
  coBorrower6SSN: SocialSecurityNumber.optional(),
  coBorrower6Details: BorrowerDetailFields.optional(),
  coBorrower7FirstName: PersonNameString.optional(),
  coBorrower7MiddleName: PersonNameString.optional(),
  coBorrower7LastName: PersonNameString.optional(),
  coBorrower7Suffix: z.string().optional().meta({
    example: 'Jr.',
  }),
  coBorrower7Phone: OptionalPhoneNumber,
  coBorrower7PhoneDetails: BorrowerPhoneFields.optional(),
  coBorrower7Email: OptionalEmailString,
  coBorrower7EmailConsent: Checkbox.optional(),
  coBorrower7MailingAddressLine1: AddressLine.optional(),
  coBorrower7MailingAddressLine2: AddressLine.optional(),
  coBorrower7MailingAddressLine3: AddressLine.optional(),
  coBorrower7MailingAddressLine4: AddressLine.optional(),
  coBorrower7MailingAddressLocality: z.string().optional(),
  coBorrower7MailingAddressRegion: z.string().optional().or(z.literal('')).meta({
    description: 'If coBorrower7MailingAddressCountry is US, this should be a two letter state abbreviation.',
    example: 'CA',
  }),
  coBorrower7MailingAddressPostcode: z.string().optional().meta({
    description: 'If country is US, this should be a 5 or 9 digit zip code.',
    example: '90210',
  }),
  coBorrower7MailingAddressCountry: CountryCode.optional().or(z.literal('')),
  coBorrower7SSN: SocialSecurityNumber.optional(),
  coBorrower7Details: BorrowerDetailFields.optional(),
  coBorrower8FirstName: PersonNameString.optional(),
  coBorrower8MiddleName: PersonNameString.optional(),
  coBorrower8LastName: PersonNameString.optional(),
  coBorrower8Suffix: z.string().optional().meta({
    example: 'Jr.',
  }),
  coBorrower8Phone: OptionalPhoneNumber,
  coBorrower8PhoneDetails: BorrowerPhoneFields.optional(),
  coBorrower8Email: OptionalEmailString,
  coBorrower8EmailConsent: Checkbox.optional(),
  coBorrower8MailingAddressLine1: AddressLine.optional(),
  coBorrower8MailingAddressLine2: AddressLine.optional(),
  coBorrower8MailingAddressLine3: AddressLine.optional(),
  coBorrower8MailingAddressLine4: AddressLine.optional(),
  coBorrower8MailingAddressLocality: z.string().optional(),
  coBorrower8MailingAddressRegion: z.string().optional().or(z.literal('')).meta({
    description: 'If coBorrower8MailingAddressCountry is US, this should be a two letter state abbreviation.',
    example: 'CA',
  }),
  coBorrower8MailingAddressPostcode: z.string().optional().meta({
    description: 'If country is US, this should be a 5 or 9 digit zip code.',
    example: '90210',
  }),
  coBorrower8MailingAddressCountry: CountryCode.optional().or(z.literal('')),
  coBorrower8SSN: SocialSecurityNumber.optional(),
  coBorrower8Details: BorrowerDetailFields.optional(),
  coBorrower9FirstName: PersonNameString.optional(),
  coBorrower9MiddleName: PersonNameString.optional(),
  coBorrower9LastName: PersonNameString.optional(),
  coBorrower9Suffix: z.string().optional().meta({
    example: 'Jr.',
  }),
  coBorrower9Phone: OptionalPhoneNumber,
  coBorrower9PhoneDetails: BorrowerPhoneFields.optional(),
  coBorrower9Email: OptionalEmailString,
  coBorrower9EmailConsent: Checkbox.optional(),
  coBorrower9MailingAddressLine1: AddressLine.optional(),
  coBorrower9MailingAddressLine2: AddressLine.optional(),
  coBorrower9MailingAddressLine3: AddressLine.optional(),
  coBorrower9MailingAddressLine4: AddressLine.optional(),
  coBorrower9MailingAddressLocality: z.string().optional(),
  coBorrower9MailingAddressRegion: z.string().optional().or(z.literal('')).meta({
    description: 'If coBorrower9MailingAddressCountry is US, this should be a two letter state abbreviation.',
    example: 'CA',
  }),
  coBorrower9MailingAddressPostcode: z.string().optional().meta({
    description: 'If country is US, this should be a 5 or 9 digit zip code.',
    example: '90210',
  }),
  coBorrower9MailingAddressCountry: CountryCode.optional().or(z.literal('')),
  coBorrower9SSN: SocialSecurityNumber.optional(),
  coBorrower9Details: BorrowerDetailFields.optional(),
  coBorrower10FirstName: PersonNameString.optional(),
  coBorrower10MiddleName: PersonNameString.optional(),
  coBorrower10LastName: PersonNameString.optional(),
  coBorrower10Suffix: z.string().optional().meta({
    example: 'Jr.',
  }),
  coBorrower10Phone: OptionalPhoneNumber,
  coBorrower10PhoneDetails: BorrowerPhoneFields.optional(),
  coBorrower10Email: OptionalEmailString,
  coBorrower10EmailConsent: Checkbox.optional(),
  coBorrower10MailingAddressLine1: AddressLine.optional(),
  coBorrower10MailingAddressLine2: AddressLine.optional(),
  coBorrower10MailingAddressLine3: AddressLine.optional(),
  coBorrower10MailingAddressLine4: AddressLine.optional(),
  coBorrower10MailingAddressLocality: z.string().optional(),
  coBorrower10MailingAddressRegion: z.string().optional().or(z.literal('')).meta({
    description: 'If coBorrower10MailingAddressCountry is US, this should be a two letter state abbreviation.',
    example: 'CA',
  }),
  coBorrower10MailingAddressPostcode: z.string().optional().meta({
    description: 'If country is US, this should be a 5 or 9 digit zip code.',
    example: '90210',
  }),
  coBorrower10MailingAddressCountry: CountryCode.optional().or(z.literal('')),
  coBorrower10SSN: SocialSecurityNumber.optional(),
  coBorrower10Details: BorrowerDetailFields.optional(),
});

export type BorrowerFields = z.infer<typeof BorrowerFields>;
export type CoborrowerFields = z.infer<typeof CoborrowerFields>;

export const AllBorrowerFields = z.object({
  ...BorrowerFields.shape,
  ...CoborrowerFields.shape,
});
export type AllBorrowerFields = z.infer<typeof AllBorrowerFields>;

export type SingleCoBorrowerFields = Pick<
  CoborrowerFields,
  | 'coBorrower1FirstName'
  | 'coBorrower1MiddleName'
  | 'coBorrower1LastName'
  | 'coBorrower1Suffix'
  | 'coBorrower1Phone'
  | 'coBorrower1PhoneDetails'
  | 'coBorrower1Email'
  | 'coBorrower1EmailConsent'
  | 'coBorrower1MailingAddressLine1'
  | 'coBorrower1MailingAddressLine2'
  | 'coBorrower1MailingAddressLine3'
  | 'coBorrower1MailingAddressLine4'
  | 'coBorrower1MailingAddressLocality'
  | 'coBorrower1MailingAddressRegion'
  | 'coBorrower1MailingAddressPostcode'
  | 'coBorrower1MailingAddressCountry'
  | 'coBorrower1SSN'
  | 'coBorrower1Details'
>;
